// C10 — SKUs y Capacidades (coordinador).
//
// Rediseño a partir de los mockups de Figma:
//   - Header con título "SKUs y Capacidades" + subtítulo informativo +
//     acciones top-right ("Importar Excel" outline deshabilitado + "Crear SKU").
//   - Dos tabs: "Catálogo de SKUs" (listado) | "Matriz capacidades" (grilla).
//   - Catálogo: barra de filtros (búsqueda + cliente + formato + estado) +
//     tabla clickeable. Click en fila → Drawer "Editar SKU".
//   - Drawer reemplaza el antiguo Dialog centrado: edita info básica +
//     capacidades por línea (configuradas con check editables + candidatas).
//   - Matriz: grilla SKU × línea, header de columnas y primera columna sticky.
//
// Tokens semánticos para tema claro/oscuro (sin colores hardcoded). Números
// con toLocaleString('es-CO') + tabular-nums.

import { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from 'sonner';
import type {
  CoordinadorSkusMatriz,
  PrecioHistoricoItem,
  SkuConfig,
  SkuLineaCapacidad,
  SkusListQuery,
} from 'schemas';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '../../components/ui/drawer';
import { Icon } from '../../components/ui/icon';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select } from '../../components/ui/select';
import { Spinner } from '../../components/ui/spinner';
import { Tab, Tabs } from '../../components/ui/tabs';
import { CoordinadorSidebar } from '../../components/layout/CoordinadorSidebar';
import { RoleLayout } from '../../components/layout/RoleLayout';
import {
  useCoordinadorSkuDetalle,
  useCoordinadorSkusList,
  useCoordinadorSkusMatriz,
  useCostoManoObra,
  useCrearSku,
  useEditarSku,
  useEliminarSkuLineaCapacidad,
  useFijarCostoManoObra,
  useFijarPrecioSku,
  useMe,
  useUpsertSkuLineaCapacidad,
} from '../../lib/api/queries';
import { useAuth } from '../../lib/auth';
import { cn } from '../../lib/utils';
import { C10ImportarExcelDialog } from './C10ImportarExcelDialog';

const fmtNum = (n: number) => n.toLocaleString('es-CO');
const fmtCop = (n: number) => `$${n.toLocaleString('es-CO')}`;

type TabKey = 'catalogo' | 'matriz';

export function C10ConfigSkusPage() {
  const { state } = useAuth();
  const [tab, setTab] = useState<TabKey>('catalogo');
  const [filtros, setFiltros] = useState<SkusListQuery>({ activo: 'true' });
  const [formato, setFormato] = useState<string>('');
  const [modalNuevo, setModalNuevo] = useState(false);
  const [modalImportar, setModalImportar] = useState(false);
  const [modalCostoMO, setModalCostoMO] = useState(false);
  const [editingSku, setEditingSku] = useState<SkuConfig | null>(null);

  const me = useMe();
  const puedeVerCostos = me.data?.ctx.puede_ver_costos ?? false;

  const { data, isLoading, isError, error } = useCoordinadorSkusList(filtros);
  // Líneas del scope: las reusamos del endpoint de matriz (ya las devuelve),
  // para poblar el filtro "Línea" del Catálogo sin tocar el endpoint de lista.
  const matriz = useCoordinadorSkusMatriz();
  const lineasScope = matriz.data?.lineas ?? [];

  if (state.kind !== 'signed_in') return <Navigate to="/login" replace />;

  if (isLoading) {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center gap-3 bg-surface-base p-6 text-fg">
        <Spinner className="size-6" />
        <p className="text-sm text-fg-secondary">Cargando SKUs…</p>
      </main>
    );
  }

  if (isError || !data) {
    return (
      <main className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center gap-3 bg-surface-base p-6 text-center text-fg">
        <h1 className="text-lg font-medium text-crit">No pudimos cargar</h1>
        <p className="text-sm text-fg-secondary">
          {error instanceof Error ? error.message : 'Error desconocido'}
        </p>
      </main>
    );
  }

  // Formatos únicos para el filtro (derivados del dataset cargado).
  const formatos = Array.from(new Set(data.skus.map((s) => s.formato))).sort();

  // Filtro formato client-side (no existe en el query del backend).
  const skusVisibles = formato ? data.skus.filter((s) => s.formato === formato) : data.skus;

  return (
    <RoleLayout sidebar={<CoordinadorSidebar />} roleLabel="Coordinador">
      <div className="flex flex-col gap-4">
        <header className="flex flex-col gap-3 border-b border-border pb-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="text-xl font-medium text-fg">SKUs y Capacidades</h1>
            <p className="mt-1 text-[11px] text-fg-tertiary">
              {data.tenant.nombre} · {data.total} SKU(s) en catálogo · matriz de capacidades
            </p>
          </div>
          <div className="flex items-center gap-2">
            {puedeVerCostos && <CostoMoPill onClick={() => setModalCostoMO(true)} />}
            <Button
              variant="outline"
              disabled={!matriz.data}
              title={
                matriz.data ? 'Importar o exportar la matriz de capacidades' : 'Cargando matriz…'
              }
              onClick={() => setModalImportar(true)}
            >
              Excel (importar / exportar)
            </Button>
            <Button variant="primary" onClick={() => setModalNuevo(true)}>
              Crear SKU
            </Button>
          </div>
        </header>

        <Tabs value={tab} onChange={(v) => setTab(v as TabKey)}>
          <Tab value="catalogo">Catálogo de SKUs</Tab>
          <Tab value="matriz">Matriz capacidades</Tab>
        </Tabs>

        {tab === 'catalogo' ? (
          <CatalogoTab
            data={data}
            skus={skusVisibles}
            formatos={formatos}
            lineas={lineasScope}
            filtros={filtros}
            setFiltros={setFiltros}
            formato={formato}
            setFormato={setFormato}
            onRowClick={setEditingSku}
            puedeVerCostos={puedeVerCostos}
          />
        ) : (
          <MatrizTab />
        )}
      </div>

      {modalNuevo && (
        <NuevoSkuDialog clientes={data.recursos.clientes} onClose={() => setModalNuevo(false)} />
      )}
      {modalImportar && matriz.data && (
        <C10ImportarExcelDialog
          data={matriz.data}
          puedeVerCostos={puedeVerCostos}
          onClose={() => setModalImportar(false)}
        />
      )}
      {modalCostoMO && <CostoManoObraDialog onClose={() => setModalCostoMO(false)} />}
      {editingSku && (
        <EditarSkuDrawer
          sku={editingSku}
          puedeVerCostos={puedeVerCostos}
          onClose={() => setEditingSku(null)}
        />
      )}
    </RoleLayout>
  );
}

// =============================================================================
// Tab: Catálogo de SKUs (listado)
// =============================================================================

function CatalogoTab({
  data,
  skus,
  formatos,
  lineas,
  filtros,
  setFiltros,
  formato,
  setFormato,
  onRowClick,
  puedeVerCostos,
}: {
  data: { recursos: { clientes: Array<{ id: string; nombre: string }> } };
  skus: SkuConfig[];
  formatos: string[];
  lineas: Array<{ id: string; codigo: string; nombre: string }>;
  filtros: SkusListQuery;
  setFiltros: (f: SkusListQuery) => void;
  formato: string;
  setFormato: (f: string) => void;
  onRowClick: (s: SkuConfig) => void;
  puedeVerCostos: boolean;
}) {
  return (
    <>
      <div className="flex flex-col gap-3 rounded border border-border bg-surface-overlay/40 p-3 sm:flex-row sm:items-end">
        <div className="flex flex-1 flex-col gap-1">
          <Label htmlFor="c10-q">Buscar</Label>
          <Input
            id="c10-q"
            placeholder="Buscar por código o nombre…"
            value={filtros.q ?? ''}
            onChange={(e) => setFiltros({ ...filtros, q: e.target.value || undefined })}
          />
        </div>
        <div className="flex flex-col gap-1 sm:w-40">
          <Label htmlFor="c10-cliente">Cliente</Label>
          <Select
            id="c10-cliente"
            value={filtros.cliente_id ?? ''}
            onChange={(e) => setFiltros({ ...filtros, cliente_id: e.target.value || undefined })}
          >
            <option value="">Todos</option>
            {data.recursos.clientes.map((c) => (
              <option key={c.id} value={c.id}>
                {c.nombre}
              </option>
            ))}
          </Select>
        </div>
        <div className="flex flex-col gap-1 sm:w-44">
          <Label htmlFor="c10-linea">Línea</Label>
          <Select
            id="c10-linea"
            value={filtros.linea_id ?? ''}
            onChange={(e) => setFiltros({ ...filtros, linea_id: e.target.value || undefined })}
          >
            <option value="">Todas</option>
            {lineas.map((l) => (
              <option key={l.id} value={l.id}>
                {l.nombre}
              </option>
            ))}
          </Select>
        </div>
        <div className="flex flex-col gap-1 sm:w-36">
          <Label htmlFor="c10-formato">Formato</Label>
          <Select id="c10-formato" value={formato} onChange={(e) => setFormato(e.target.value)}>
            <option value="">Todos</option>
            {formatos.map((f) => (
              <option key={f} value={f}>
                {f}
              </option>
            ))}
          </Select>
        </div>
        <div className="flex flex-col gap-1 sm:w-36">
          <Label htmlFor="c10-activo">Estado</Label>
          <Select
            id="c10-activo"
            value={filtros.activo ?? ''}
            onChange={(e) =>
              setFiltros({
                ...filtros,
                activo: (e.target.value as 'true' | 'false' | '') || undefined,
              })
            }
          >
            <option value="true">Activos</option>
            <option value="false">Inactivos</option>
            <option value="">Todos</option>
          </Select>
        </div>
      </div>

      {skus.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-sm text-fg-tertiary">
            Sin SKUs que matcheen los filtros.
          </CardContent>
        </Card>
      ) : (
        <div className="max-h-[70vh] overflow-auto rounded border border-border">
          <table className="w-full border-collapse text-sm">
            <thead className="sticky top-0 z-10">
              <tr className="border-b border-border bg-surface-overlay text-left text-[10px] font-medium uppercase tracking-wider text-fg-tertiary">
                <th className="px-4 py-2.5">Código</th>
                <th className="px-4 py-2.5">Descripción</th>
                <th className="px-4 py-2.5">Cliente</th>
                <th className="px-4 py-2.5">Formato</th>
                <th className="px-4 py-2.5 text-right">Líneas</th>
                <th className="px-4 py-2.5 text-right">Cap. estándar</th>
                {puedeVerCostos && <th className="px-4 py-2.5 text-right">Precio</th>}
                <th className="px-4 py-2.5 text-center">Estado</th>
              </tr>
            </thead>
            <tbody>
              {skus.map((s) => (
                <tr
                  key={s.id}
                  onClick={() => onRowClick(s)}
                  className="cursor-pointer border-b border-border last:border-0 transition-colors hover:bg-surface-overlay/40"
                >
                  <td className="px-4 py-2.5">
                    <span className="font-medium text-fg">{s.codigo_cliente}</span>
                    {s.codigo_interno && (
                      <div className="text-xs text-fg-tertiary">{s.codigo_interno}</div>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-fg">{s.descripcion}</td>
                  <td className="px-4 py-2.5 text-fg-secondary">{s.cliente_nombre}</td>
                  <td className="px-4 py-2.5 text-fg-secondary">{s.formato}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">
                    <span className={s.lineas_configuradas === 0 ? 'text-warn' : 'text-fg'}>
                      {s.lineas_configuradas}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-fg-secondary">
                    {s.capacidad_estandar_representativa !== null ? (
                      <>
                        {fmtNum(s.capacidad_estandar_representativa)}{' '}
                        <span className="text-fg-tertiary">paq/h</span>
                      </>
                    ) : (
                      <span className="text-fg-tertiary">——</span>
                    )}
                  </td>
                  {puedeVerCostos && (
                    <td className="px-4 py-2.5 text-right tabular-nums">
                      {s.precio_vigente !== null ? (
                        <>
                          <span className="text-fg">{fmtCop(s.precio_vigente)}</span>{' '}
                          <span className="text-fg-tertiary">/paq</span>
                        </>
                      ) : (
                        <span className="text-fg-tertiary">— Sin precio</span>
                      )}
                    </td>
                  )}
                  <td className="px-4 py-2.5 text-center">
                    <Badge tone={s.activo ? 'ok' : 'neutral'} dot>
                      {s.activo ? 'Activo' : 'Inactivo'}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

// =============================================================================
// Tab: Matriz capacidades (grilla SKU × línea)
// =============================================================================

function MatrizTab() {
  const { data, isLoading, isError, error } = useCoordinadorSkusMatriz();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Spinner className="size-5" />
      </div>
    );
  }
  if (isError || !data) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-sm text-crit">
          {error instanceof Error ? error.message : 'No pudimos cargar la matriz.'}
        </CardContent>
      </Card>
    );
  }
  if (data.lineas.length === 0 || data.filas.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-sm text-fg-tertiary">
          Sin datos para la matriz (no hay líneas en tu scope o SKUs activos).
        </CardContent>
      </Card>
    );
  }

  return <MatrizGrid data={data} />;
}

/** Celda seleccionada para editar/crear capacidad desde la matriz. */
type CeldaSel = {
  skuId: string;
  skuCodigo: string;
  skuDescripcion: string;
  lineaId: string;
  lineaNombre: string;
  capActual: number | null;
};

function MatrizGrid({ data }: { data: CoordinadorSkusMatriz }) {
  const [celda, setCelda] = useState<CeldaSel | null>(null);

  return (
    <>
      <div className="-mt-1 mb-2 flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
        <p className="text-[11px] text-fg-tertiary">
          Haga clic en una celda para configurar o editar la capacidad de ese SKU en esa línea.
        </p>
        <p className="flex items-center gap-1 text-[11px] font-medium text-fg-secondary">
          <Icon name="arrow-right" className="size-3.5 animate-pulse" />
          Deslizá horizontalmente para ver las {data.lineas.length} líneas
        </p>
      </div>
      <div className="relative max-h-[calc(100vh-16rem)] overflow-auto rounded border border-border">
        <table className="border-collapse text-sm">
          <thead>
            <tr>
              <th className="sticky left-0 top-0 z-30 min-w-[14rem] border-b border-r-2 border-border-strong bg-surface-overlay px-4 py-2.5 text-left text-[10px] font-medium uppercase tracking-wider text-fg-tertiary">
                SKU \ Línea
              </th>
              {data.lineas.map((l) => (
                <th
                  key={l.id}
                  className="sticky top-0 z-20 min-w-[6.5rem] border-b border-l border-border bg-surface-overlay px-3 py-2.5 text-right align-bottom text-[11px] font-medium leading-tight text-fg-secondary"
                  title={l.codigo}
                >
                  {l.nombre}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.filas.map((fila, idx) => {
              const zebra = idx % 2 === 1;
              return (
                <tr
                  key={fila.sku_id}
                  className={cn(
                    'group border-b border-border last:border-0',
                    zebra ? 'bg-surface-elevated' : 'bg-surface-base',
                  )}
                >
                  <td
                    className={cn(
                      'sticky left-0 z-10 min-w-[14rem] border-r-2 border-border-strong px-4 py-2.5 transition-colors',
                      zebra ? 'bg-surface-elevated' : 'bg-surface-base',
                      'group-hover:bg-surface-overlay',
                    )}
                  >
                    <div className="font-medium text-fg">{fila.codigo_cliente}</div>
                    <div className="truncate text-xs text-fg-tertiary">{fila.descripcion}</div>
                  </td>
                  {data.lineas.map((l) => {
                    const cap = fila.capacidades[l.id];
                    const configurada = cap !== null && cap !== undefined;
                    return (
                      <td
                        key={l.id}
                        className="border-l border-border/50 p-0 transition-colors group-hover:bg-accent-soft"
                      >
                        <button
                          type="button"
                          onClick={() =>
                            setCelda({
                              skuId: fila.sku_id,
                              skuCodigo: fila.codigo_cliente,
                              skuDescripcion: fila.descripcion,
                              lineaId: l.id,
                              lineaNombre: l.nombre,
                              capActual: configurada ? cap : null,
                            })
                          }
                          title={configurada ? 'Editar capacidad' : 'Configurar capacidad'}
                          className="flex h-full w-full cursor-pointer items-center justify-end px-3 py-2.5 text-right tabular-nums transition-colors hover:bg-accent/15 focus:bg-accent/15 focus:outline-none"
                        >
                          {configurada ? (
                            <span className="font-medium text-fg">{fmtNum(cap)}</span>
                          ) : (
                            <span className="text-fg-disabled">——</span>
                          )}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {celda && <MatrizCeldaDialog celda={celda} onClose={() => setCelda(null)} />}
    </>
  );
}

// =============================================================================
// Dialog: configurar/editar capacidad de una celda de la matriz
// =============================================================================

function MatrizCeldaDialog({ celda, onClose }: { celda: CeldaSel; onClose: () => void }) {
  const upsert = useUpsertSkuLineaCapacidad(celda.skuId);
  const eliminar = useEliminarSkuLineaCapacidad(celda.skuId);
  // El detalle trae la config completa de la línea (personas, golpes, bache),
  // que la matriz no expone. Lo usamos para precargar correctamente al editar.
  const { data: detalle, isLoading } = useCoordinadorSkuDetalle(celda.skuId);
  const lineaConfig = detalle?.lineas_configuradas.find((l) => l.linea_id === celda.lineaId);

  const yaConfigurada = celda.capActual !== null;

  const [paqh, setPaqh] = useState(celda.capActual !== null ? String(celda.capActual) : '');
  const [pers, setPers] = useState('1');
  const [golpe, setGolpe] = useState('');
  const [golpeAplica, setGolpeAplica] = useState(false);
  const hidratado = useRef(false);

  // Cuando llega el detalle, precargamos personas/golpes una sola vez.
  useEffect(() => {
    if (!lineaConfig || hidratado.current) return;
    setPers(String(lineaConfig.personas_estandar_turno));
    setGolpeAplica(lineaConfig.golpes_min_aplica);
    setGolpe(
      lineaConfig.capacidad_estandar_golpes_min !== null
        ? String(lineaConfig.capacidad_estandar_golpes_min)
        : '',
    );
    if (celda.capActual === null) setPaqh(String(lineaConfig.capacidad_estandar_unidades_hora));
    hidratado.current = true;
  }, [lineaConfig, celda.capActual]);

  const guardar = async () => {
    const paqhNum = Number(paqh);
    const persNum = Number(pers);
    if (!paqhNum || paqhNum <= 0) {
      toast.error('Capacidad (paq/h) debe ser > 0');
      return;
    }
    if (!persNum || persNum < 1 || persNum > 50) {
      toast.error('Personas por turno debe estar entre 1 y 50');
      return;
    }
    if (golpeAplica && (!golpe || Number(golpe) <= 0)) {
      toast.error('Golpes/min debe ser > 0 si aplica');
      return;
    }
    try {
      await upsert.mutateAsync({
        lineaId: celda.lineaId,
        body: {
          capacidad_estandar_unidades_hora: paqhNum,
          personas_estandar_turno: persNum,
          bache_minimo: lineaConfig?.bache_minimo ?? null,
          golpes_min_aplica: golpeAplica,
          capacidad_estandar_golpes_min: golpeAplica ? Number(golpe) : null,
        },
      });
      toast.success(`Capacidad de ${celda.lineaNombre} guardada`);
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Error al guardar');
    }
  };

  const quitar = async () => {
    if (!confirm(`¿Quitar la capacidad de ${celda.skuCodigo} en ${celda.lineaNombre}?`)) return;
    try {
      await eliminar.mutateAsync(celda.lineaId);
      toast.success('Capacidad quitada');
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Error al quitar');
    }
  };

  return (
    <Dialog open onClose={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{yaConfigurada ? 'Editar capacidad' : 'Configurar capacidad'}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <div className="rounded border border-border bg-surface-overlay/40 px-3 py-2 text-xs">
            <div className="font-medium text-fg">{celda.skuCodigo}</div>
            <div className="truncate text-fg-tertiary">{celda.skuDescripcion}</div>
            <div className="mt-1 text-fg-secondary">
              Línea: <span className="text-fg">{celda.lineaNombre}</span>
            </div>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-4">
              <Spinner className="size-5" />
            </div>
          ) : (
            <>
              <div className="flex flex-col gap-1">
                <Label htmlFor="mc-paqh">Capacidad (paq/h) *</Label>
                <Input
                  id="mc-paqh"
                  type="number"
                  min="0"
                  className="tabular-nums"
                  value={paqh}
                  onChange={(e) => setPaqh(e.target.value)}
                  placeholder="Ej: 1200"
                />
              </div>
              <div className="flex flex-col gap-1">
                <Label htmlFor="mc-pers">Personas por turno *</Label>
                <Input
                  id="mc-pers"
                  type="number"
                  min="1"
                  max="50"
                  className="tabular-nums"
                  value={pers}
                  onChange={(e) => setPers(e.target.value)}
                />
              </div>
              <label className="flex items-center gap-2 text-sm text-fg-secondary">
                <input
                  type="checkbox"
                  checked={golpeAplica}
                  onChange={(e) => setGolpeAplica(e.target.checked)}
                />
                La línea maneja golpes/min
              </label>
              {golpeAplica && (
                <div className="flex flex-col gap-1">
                  <Label htmlFor="mc-golpe">Golpes/min *</Label>
                  <Input
                    id="mc-golpe"
                    type="number"
                    min="0"
                    className="tabular-nums"
                    value={golpe}
                    onChange={(e) => setGolpe(e.target.value)}
                  />
                </div>
              )}
            </>
          )}
        </div>
        <DialogFooter className="justify-between">
          {yaConfigurada ? (
            <Button variant="outline" onClick={quitar} loading={eliminar.isPending}>
              Quitar
            </Button>
          ) : (
            <span />
          )}
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={guardar}
              loading={upsert.isPending}
              disabled={isLoading}
            >
              Guardar
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================
// Dialog: Nuevo SKU (se mantiene como modal centrado)
// =============================================================================

function NuevoSkuDialog({
  clientes,
  onClose,
}: {
  clientes: Array<{ id: string; nombre: string }>;
  onClose: () => void;
}) {
  const crear = useCrearSku();
  const [form, setForm] = useState({
    cliente_id: clientes[0]?.id ?? '',
    codigo_cliente: '',
    codigo_interno: '',
    descripcion: '',
    formato: '',
  });

  const submit = async () => {
    if (!form.cliente_id || !form.codigo_cliente || !form.descripcion || !form.formato) {
      toast.error('Complete todos los campos requeridos');
      return;
    }
    try {
      await crear.mutateAsync({
        cliente_id: form.cliente_id,
        codigo_cliente: form.codigo_cliente,
        codigo_interno: form.codigo_interno || null,
        descripcion: form.descripcion,
        formato: form.formato,
      });
      toast.success('SKU creado');
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Error al crear');
    }
  };

  return (
    <Dialog open onClose={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Crear SKU</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <Label htmlFor="new-cliente">Cliente *</Label>
            <Select
              id="new-cliente"
              value={form.cliente_id}
              onChange={(e) => setForm({ ...form, cliente_id: e.target.value })}
            >
              {clientes.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nombre}
                </option>
              ))}
            </Select>
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="new-codigo">Código del cliente *</Label>
            <Input
              id="new-codigo"
              value={form.codigo_cliente}
              onChange={(e) => setForm({ ...form, codigo_cliente: e.target.value })}
              placeholder="Ej: 10148"
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="new-codint">Código interno (opcional)</Label>
            <Input
              id="new-codint"
              value={form.codigo_interno}
              onChange={(e) => setForm({ ...form, codigo_interno: e.target.value })}
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="new-desc">Descripción *</Label>
            <Input
              id="new-desc"
              value={form.descripcion}
              onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="new-formato">Formato *</Label>
            <Input
              id="new-formato"
              value={form.formato}
              onChange={(e) => setForm({ ...form, formato: e.target.value })}
              placeholder="X6, X8, X10, LECHES..."
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={submit} loading={crear.isPending}>
            Crear
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================
// Drawer: Editar SKU + capacidades por línea
// =============================================================================

function EditarSkuDrawer({
  sku,
  puedeVerCostos,
  onClose,
}: {
  sku: SkuConfig;
  puedeVerCostos: boolean;
  onClose: () => void;
}) {
  const { data, isLoading } = useCoordinadorSkuDetalle(sku.id);
  const editar = useEditarSku(sku.id);

  const [form, setForm] = useState({
    codigo_interno: sku.codigo_interno ?? '',
    descripcion: sku.descripcion,
    formato: sku.formato,
  });

  const guardar = async () => {
    try {
      await editar.mutateAsync({
        codigo_interno: form.codigo_interno || null,
        descripcion: form.descripcion,
        formato: form.formato,
      });
      toast.success('SKU actualizado');
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Error al editar');
    }
  };

  const desactivar = async () => {
    if (!confirm(`¿Desactivar el SKU ${sku.codigo_cliente}?`)) return;
    try {
      await editar.mutateAsync({ activo: false });
      toast.success('SKU desactivado');
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Error');
    }
  };

  return (
    <Drawer open onClose={onClose} width={560}>
      <DrawerHeader>
        <div className="min-w-0">
          <DrawerTitle>Editar SKU</DrawerTitle>
          <p className="mt-0.5 truncate text-[11px] text-fg-tertiary">
            {sku.codigo_cliente} · creado {sku.created_at.slice(0, 10)} · modificado{' '}
            {sku.updated_at.slice(0, 10)}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Cerrar"
          className="rounded p-1 text-fg-tertiary transition-colors hover:bg-surface-overlay hover:text-fg"
        >
          <Icon name="x" />
        </button>
      </DrawerHeader>

      <DrawerBody className="flex flex-col gap-6">
        {/* Sección: información básica */}
        <section className="flex flex-col gap-3">
          <h3 className="text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
            Información básica
          </h3>
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1">
              <Label>Código</Label>
              <Input value={sku.codigo_cliente} disabled readOnly />
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor="ed-codint">Código interno</Label>
              <Input
                id="ed-codint"
                value={form.codigo_interno}
                onChange={(e) => setForm({ ...form, codigo_interno: e.target.value })}
              />
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="ed-desc">Descripción</Label>
            <Input
              id="ed-desc"
              value={form.descripcion}
              onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1">
              <Label>Cliente</Label>
              <Input value={sku.cliente_nombre} disabled readOnly />
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor="ed-formato">Formato</Label>
              <Input
                id="ed-formato"
                value={form.formato}
                onChange={(e) => setForm({ ...form, formato: e.target.value })}
              />
            </div>
          </div>
        </section>

        {/* Sección: precio (capacidad puede_ver_costos) */}
        {puedeVerCostos && (
          <section className="flex flex-col gap-3">
            <h3 className="text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
              Precio
            </h3>
            <PrecioSection
              skuId={sku.id}
              precioVigente={data?.sku.precio_vigente ?? sku.precio_vigente}
              precioVigenteDesde={data?.sku.precio_vigente_desde ?? sku.precio_vigente_desde}
              historico={data?.precio_historico ?? []}
            />
          </section>
        )}

        {/* Sección: capacidades por línea */}
        <section className="flex flex-col gap-3">
          <h3 className="text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
            Capacidades por línea
          </h3>
          {isLoading || !data ? (
            <div className="flex items-center justify-center py-6">
              <Spinner className="size-5" />
            </div>
          ) : (
            <CapacidadesLista skuId={sku.id} data={data} />
          )}
        </section>

        {/* TODO: nota requiere migración — el mockup muestra una nota/observación
            libre por SKU, pero NO existe campo `nota` en el schema. */}
      </DrawerBody>

      <DrawerFooter className="justify-between">
        <Button variant="outline" onClick={desactivar} disabled={!sku.activo}>
          Desactivar SKU
        </Button>
        <div className="flex items-center gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={guardar} loading={editar.isPending}>
            Guardar cambios
          </Button>
        </div>
      </DrawerFooter>
    </Drawer>
  );
}

// =============================================================================
// Lista de capacidades por línea dentro del Drawer
// =============================================================================

type LineaRow =
  | { kind: 'config'; linea: SkuLineaCapacidad }
  | {
      kind: 'candidata';
      linea: { linea_id: string; linea_codigo: string; linea_nombre: string };
    };

function CapacidadesLista({
  skuId,
  data,
}: {
  skuId: string;
  data: {
    lineas_configuradas: SkuLineaCapacidad[];
    lineas_candidatas: Array<{
      linea_id: string;
      linea_codigo: string;
      linea_nombre: string;
    }>;
  };
}) {
  const upsert = useUpsertSkuLineaCapacidad(skuId);
  const eliminar = useEliminarSkuLineaCapacidad(skuId);

  const rows: LineaRow[] = useMemo(
    () => [
      ...data.lineas_configuradas.map((l) => ({ kind: 'config' as const, linea: l })),
      ...data.lineas_candidatas.map((l) => ({ kind: 'candidata' as const, linea: l })),
    ],
    [data],
  );

  // Estado editable local por línea configurada (paq/h, personas, golpes/min, aplica golpes).
  const [draft, setDraft] = useState<
    Record<string, { paqh: string; pers: string; golpe: string; golpeAplica: boolean }>
  >({});

  const draftFromLinea = (lineaId: string) => {
    const l = data.lineas_configuradas.find((x) => x.linea_id === lineaId);
    return {
      paqh: l ? String(l.capacidad_estandar_unidades_hora) : '',
      pers: l ? String(l.personas_estandar_turno) : '1',
      golpe:
        l?.capacidad_estandar_golpes_min != null ? String(l.capacidad_estandar_golpes_min) : '',
      golpeAplica: l?.golpes_min_aplica ?? false,
    };
  };

  const getDraft = (l: SkuLineaCapacidad) => draft[l.linea_id] ?? draftFromLinea(l.linea_id);

  const setField = (lineaId: string, field: 'paqh' | 'pers' | 'golpe', value: string) =>
    setDraft((d) => ({
      ...d,
      [lineaId]: { ...(d[lineaId] ?? draftFromLinea(lineaId)), [field]: value },
    }));

  const setGolpeAplica = (lineaId: string, checked: boolean) =>
    setDraft((d) => ({
      ...d,
      [lineaId]: { ...(d[lineaId] ?? draftFromLinea(lineaId)), golpeAplica: checked },
    }));

  const guardarLinea = async (l: SkuLineaCapacidad) => {
    const dr = getDraft(l);
    const paqh = Number(dr.paqh);
    const pers = Number(dr.pers);
    if (!paqh || paqh <= 0) {
      toast.error('Capacidad (paq/h) debe ser > 0');
      return;
    }
    if (!pers || pers < 1) {
      toast.error('Personas debe ser ≥ 1');
      return;
    }
    const aplicaGolpes = dr.golpeAplica;
    if (aplicaGolpes && (!dr.golpe || Number(dr.golpe) <= 0)) {
      toast.error('Golpes/min debe ser > 0 (o desmarque "maneja golpes")');
      return;
    }
    try {
      await upsert.mutateAsync({
        lineaId: l.linea_id,
        body: {
          capacidad_estandar_unidades_hora: paqh,
          personas_estandar_turno: pers,
          bache_minimo: l.bache_minimo,
          golpes_min_aplica: aplicaGolpes,
          capacidad_estandar_golpes_min: aplicaGolpes && dr.golpe ? Number(dr.golpe) : null,
        },
      });
      toast.success(`Capacidad de ${l.linea_codigo} guardada`);
      setDraft((d) => {
        const next = { ...d };
        delete next[l.linea_id];
        return next;
      });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Error');
    }
  };

  const activarCandidata = async (lineaId: string, lineaCodigo: string) => {
    // Upsert con valores por defecto al chequear; el coordinador ajusta luego.
    try {
      await upsert.mutateAsync({
        lineaId,
        body: {
          capacidad_estandar_unidades_hora: 1,
          personas_estandar_turno: 1,
          bache_minimo: null,
          golpes_min_aplica: false,
          capacidad_estandar_golpes_min: null,
        },
      });
      toast.success(`Línea ${lineaCodigo} agregada — ajuste su capacidad`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Error');
    }
  };

  const quitarLinea = async (lineaId: string, lineaCodigo: string) => {
    if (!confirm(`¿Quitar la línea ${lineaCodigo} de este SKU?`)) return;
    try {
      await eliminar.mutateAsync(lineaId);
      toast.success(`Línea ${lineaCodigo} quitada`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Error');
    }
  };

  if (rows.length === 0) {
    return (
      <p className="py-4 text-center text-sm text-fg-tertiary">
        No hay líneas en tu scope para este SKU.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-1.5">
      {/* Encabezado de columnas */}
      <div className="grid grid-cols-[1fr_4.5rem_3.5rem_6.5rem_2rem] items-center gap-2 px-1 text-[10px] font-medium uppercase tracking-wider text-fg-tertiary">
        <span>Línea</span>
        <span className="text-right">Paq/h</span>
        <span className="text-right">Pers</span>
        <span className="text-right">Golpe</span>
        <span />
      </div>

      {rows.map((row) => {
        if (row.kind === 'config') {
          const l = row.linea;
          const dr = getDraft(l);
          const dirty = Boolean(draft[l.linea_id]);
          return (
            <div
              key={l.linea_id}
              className="grid grid-cols-[1fr_4.5rem_3.5rem_6.5rem_2rem] items-center gap-2 rounded border border-border bg-surface-base px-2 py-1.5"
            >
              <div className="flex min-w-0 items-center gap-2">
                <span className="flex size-4 flex-shrink-0 items-center justify-center rounded-sm bg-accent text-accent-ink">
                  <Icon name="check" size={12} />
                </span>
                <span className="truncate text-sm text-fg" title={l.linea_nombre}>
                  {l.linea_nombre}
                </span>
              </div>
              <Input
                className="h-8 px-2 text-right text-sm tabular-nums"
                type="number"
                min="0"
                value={dr.paqh}
                onChange={(e) => setField(l.linea_id, 'paqh', e.target.value)}
              />
              <Input
                className="h-8 px-2 text-right text-sm tabular-nums"
                type="number"
                min="1"
                value={dr.pers}
                onChange={(e) => setField(l.linea_id, 'pers', e.target.value)}
              />
              <div className="flex items-center justify-end gap-1">
                <input
                  type="checkbox"
                  className="size-3.5 flex-shrink-0 cursor-pointer"
                  checked={dr.golpeAplica}
                  title="La línea maneja golpes/min"
                  aria-label="La línea maneja golpes/min"
                  onChange={(e) => setGolpeAplica(l.linea_id, e.target.checked)}
                />
                <Input
                  className="h-8 w-12 px-2 text-right text-sm tabular-nums"
                  type="number"
                  min="0"
                  value={dr.golpe}
                  disabled={!dr.golpeAplica}
                  placeholder={dr.golpeAplica ? '' : 'N/A'}
                  onChange={(e) => setField(l.linea_id, 'golpe', e.target.value)}
                />
              </div>
              <div className="flex items-center justify-end">
                {dirty ? (
                  <button
                    type="button"
                    title="Guardar línea"
                    onClick={() => guardarLinea(l)}
                    className="rounded p-1 text-ok transition-colors hover:bg-surface-overlay"
                  >
                    <Icon name="check" size={14} />
                  </button>
                ) : (
                  <button
                    type="button"
                    title="Quitar línea"
                    onClick={() => quitarLinea(l.linea_id, l.linea_codigo)}
                    className="rounded p-1 text-fg-tertiary transition-colors hover:bg-surface-overlay hover:text-crit"
                  >
                    <Icon name="x" size={14} />
                  </button>
                )}
              </div>
            </div>
          );
        }

        // Candidata: sin check, fila atenuada; al chequear → upsert default.
        const l = row.linea;
        return (
          <button
            key={l.linea_id}
            type="button"
            onClick={() => activarCandidata(l.linea_id, l.linea_codigo)}
            className="grid grid-cols-[1fr_4.5rem_3.5rem_6.5rem_2rem] items-center gap-2 rounded border border-dashed border-border px-2 py-1.5 text-left transition-colors hover:bg-surface-overlay/40"
          >
            <div className="flex min-w-0 items-center gap-2">
              <span className="size-4 flex-shrink-0 rounded-sm border border-border" aria-hidden />
              <span className="truncate text-sm text-fg-tertiary" title={l.linea_nombre}>
                {l.linea_nombre}
              </span>
            </div>
            <span className="text-right text-sm text-fg-tertiary">——</span>
            <span className="text-right text-sm text-fg-tertiary">——</span>
            <span className="text-right text-sm text-fg-tertiary">——</span>
            <span />
          </button>
        );
      })}
    </div>
  );
}

// =============================================================================
// WS-5: pill + dialog de Costo MO/h-persona; sección de precio del SKU
// =============================================================================

const hoyISO = () => new Date().toISOString().slice(0, 10);

/** Pill del header: muestra el costo MO vigente y abre el dialog de edición. */
function CostoMoPill({ onClick }: { onClick: () => void }) {
  const { data } = useCostoManoObra();
  const v = data?.vigente?.valor_hora_persona_cop ?? null;
  return (
    <button
      type="button"
      onClick={onClick}
      title="Costo de mano de obra por hora-persona"
      className="flex items-center gap-2 rounded-md border border-border bg-surface-overlay/40 px-3 py-2 text-sm transition-colors hover:bg-surface-overlay"
    >
      <span className="text-fg-tertiary">Costo MO/h·persona</span>
      <span className="font-medium tabular-nums text-fg">{v !== null ? fmtCop(v) : '—'}</span>
      <Icon name="edit" size={12} className="text-fg-tertiary" />
    </button>
  );
}

function HistoricoTabla({
  rows,
}: {
  rows: Array<{
    id: string;
    valor: number;
    vigente_desde: string;
    vigente_hasta: string | null;
    autor: string | null;
  }>;
}) {
  return (
    <div className="max-h-40 overflow-auto rounded border border-border">
      <table className="w-full text-xs">
        <thead className="bg-surface-overlay text-left text-fg-tertiary">
          <tr>
            <th className="px-2 py-1">Valor</th>
            <th className="px-2 py-1">Desde</th>
            <th className="px-2 py-1">Hasta</th>
            <th className="px-2 py-1">Cambió</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((h) => (
            <tr key={h.id} className="border-t border-border">
              <td className="px-2 py-1 tabular-nums text-fg">{fmtCop(h.valor)}</td>
              <td className="px-2 py-1">{h.vigente_desde}</td>
              <td className="px-2 py-1">
                {h.vigente_hasta ?? <span className="text-ok">vigente</span>}
              </td>
              <td className="px-2 py-1 text-fg-tertiary">{h.autor ?? '—'}</td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td colSpan={4} className="px-2 py-2 text-center text-fg-tertiary">
                Sin histórico.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function CostoManoObraDialog({ onClose }: { onClose: () => void }) {
  const { data, isLoading } = useCostoManoObra();
  const fijar = useFijarCostoManoObra();
  const [valor, setValor] = useState('');
  const [desde, setDesde] = useState(hoyISO());
  const [verHist, setVerHist] = useState(false);

  const vigente = data?.vigente ?? null;

  const guardar = async () => {
    const n = Number(valor);
    if (!n || n <= 0) {
      toast.error('El valor debe ser mayor a 0');
      return;
    }
    try {
      await fijar.mutateAsync({ valor_hora_persona_cop: n, vigente_desde: desde });
      toast.success('Costo de mano de obra actualizado');
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Error al guardar');
    }
  };

  return (
    <Dialog open onClose={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Costo de mano de obra</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <div className="rounded border border-border bg-surface-overlay/40 px-3 py-2 text-sm">
            {isLoading ? (
              <Spinner className="size-4" />
            ) : vigente ? (
              <>
                <span className="text-lg font-medium tabular-nums text-fg">
                  {fmtCop(vigente.valor_hora_persona_cop)}
                </span>{' '}
                <span className="text-fg-tertiary">/hora · persona</span>
                <div className="text-[11px] text-fg-tertiary">
                  vigente desde {vigente.vigente_desde}
                </div>
              </>
            ) : (
              <span className="text-fg-tertiary">Sin costo definido todavía.</span>
            )}
          </div>

          <p className="text-[11px] text-warn">
            Se crea una nueva vigencia — el valor anterior se conserva en el histórico.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1">
              <Label htmlFor="mo-valor">Nuevo valor *</Label>
              <Input
                id="mo-valor"
                type="number"
                min="0"
                className="tabular-nums"
                value={valor}
                onChange={(e) => setValor(e.target.value)}
                placeholder="29000"
              />
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor="mo-desde">Vigente desde *</Label>
              <Input
                id="mo-desde"
                type="date"
                value={desde}
                onChange={(e) => setDesde(e.target.value)}
              />
            </div>
          </div>

          <button
            type="button"
            onClick={() => setVerHist((p) => !p)}
            className="flex items-center gap-1 self-start text-[11px] text-fg-secondary hover:text-fg"
          >
            <Icon name="clock" size={12} /> {verHist ? 'Ocultar histórico' : 'Ver histórico'}
          </button>
          {verHist && data && (
            <HistoricoTabla
              rows={data.historico.map((h) => ({
                id: h.id,
                valor: h.valor_hora_persona_cop,
                vigente_desde: h.vigente_desde,
                vigente_hasta: h.vigente_hasta,
                autor: h.registrado_por_nombre,
              }))}
            />
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={guardar} loading={fijar.isPending}>
            Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PrecioSection({
  skuId,
  precioVigente,
  precioVigenteDesde,
  historico,
}: {
  skuId: string;
  precioVigente: number | null;
  precioVigenteDesde: string | null;
  historico: PrecioHistoricoItem[];
}) {
  const fijar = useFijarPrecioSku(skuId);
  const [editando, setEditando] = useState(false);
  const [verHist, setVerHist] = useState(false);
  const [precio, setPrecio] = useState('');
  const [desde, setDesde] = useState(hoyISO());

  const guardar = async () => {
    const n = Number(precio);
    if (!n || n <= 0) {
      toast.error('El precio debe ser mayor a 0');
      return;
    }
    try {
      await fijar.mutateAsync({ precio_unitario_cop: n, vigente_desde: desde });
      toast.success('Precio actualizado');
      setEditando(false);
      setPrecio('');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Error al guardar');
    }
  };

  return (
    <div className="rounded border border-border bg-surface-base p-3">
      <div className="flex items-center justify-between gap-2">
        <div>
          {precioVigente !== null ? (
            <>
              <span className="text-lg font-medium tabular-nums text-fg">
                {fmtCop(precioVigente)}
              </span>{' '}
              <span className="text-fg-tertiary">/paq</span>
              {precioVigenteDesde && (
                <div className="text-[11px] text-fg-tertiary">
                  vigente desde {precioVigenteDesde}
                </div>
              )}
            </>
          ) : (
            <span className="text-sm text-fg-tertiary">Sin precio cargado.</span>
          )}
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setEditando((p) => !p)}
            className="flex items-center gap-1 text-[11px] text-fg-secondary hover:text-fg"
          >
            <Icon name="edit" size={12} /> Editar precio
          </button>
          <button
            type="button"
            onClick={() => setVerHist((p) => !p)}
            className="flex items-center gap-1 text-[11px] text-fg-secondary hover:text-fg"
          >
            <Icon name="clock" size={12} /> Ver histórico
          </button>
        </div>
      </div>

      {editando && (
        <div className="mt-3 flex flex-col gap-2 border-t border-border pt-3">
          <p className="text-[11px] text-warn">
            Se crea una nueva vigencia — el valor anterior se conserva en el histórico.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1">
              <Label htmlFor="pr-nuevo">Nuevo precio *</Label>
              <Input
                id="pr-nuevo"
                type="number"
                min="0"
                className="tabular-nums"
                value={precio}
                onChange={(e) => setPrecio(e.target.value)}
                placeholder="370"
              />
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor="pr-desde">Vigente desde *</Label>
              <Input
                id="pr-desde"
                type="date"
                value={desde}
                onChange={(e) => setDesde(e.target.value)}
              />
            </div>
          </div>
          <div className="flex items-center justify-end gap-2">
            <Button variant="ghost" onClick={() => setEditando(false)}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={guardar} loading={fijar.isPending}>
              Guardar precio
            </Button>
          </div>
        </div>
      )}

      {verHist && (
        <div className="mt-3">
          <HistoricoTabla
            rows={historico.map((h) => ({
              id: h.id,
              valor: h.precio_unitario_cop,
              vigente_desde: h.vigente_desde,
              vigente_hasta: h.vigente_hasta,
              autor: h.registrado_por_nombre,
            }))}
          />
        </div>
      )}
    </div>
  );
}
